import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const THEME_STORAGE_KEY = 'tears-theme';
/** Stored preferences. `system` is the unset default only — not a toggle target. */
export const THEME_OPTIONS = ['light', 'dark', 'system'];
export const THEME_TOGGLE_OPTIONS = ['light', 'dark'];

const DEFAULT_THEME_STATE = {
  preference: 'system',
  resolved: 'dark',
  setPreference: () => {},
  cyclePreference: () => {},
};

const ThemeContext = createContext(null);

function readStoredPreference() {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (THEME_OPTIONS.includes(raw)) return raw;
  } catch {
    /* ignore */
  }
  return 'system';
}

export function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function resolveTheme(preference) {
  if (preference === 'light' || preference === 'dark') return preference;
  return getSystemTheme();
}

export function applyResolvedTheme(resolved) {
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    const value = getComputedStyle(root).getPropertyValue('--theme-color').trim()
      || (resolved === 'dark' ? '#0b0d12' : '#f4f6fa');
    meta.setAttribute('content', value);
  }
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => {
    if (typeof window === 'undefined') return 'system';
    return readStoredPreference();
  });
  const [resolved, setResolved] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return resolveTheme(readStoredPreference());
  });

  const setPreference = useCallback((next) => {
    if (!THEME_OPTIONS.includes(next)) return;
    setPreferenceState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const cyclePreference = useCallback(() => {
    setPreferenceState((prev) => {
      // Toggle Light ↔ Dark only. From System, flip relative to what is currently shown.
      const next = resolveTheme(prev) === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const next = resolveTheme(preference);
    setResolved(next);
    applyResolvedTheme(next);
  }, [preference]);

  useEffect(() => {
    if (preference !== 'system' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next = resolveTheme('system');
      setResolved(next);
      applyResolvedTheme(next);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  const value = useMemo(
    () => ({ preference, resolved, setPreference, cyclePreference }),
    [preference, resolved, setPreference, cyclePreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext) || DEFAULT_THEME_STATE;
}

export default ThemeContext;
